import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { IdNegocio } from '../operaciones';
import type { components } from '../tipos/clientes';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Sugerencia = Esquema<'DuplicateSuggestion'>;
export type ListaSugerencias = Esquema<'DuplicateSuggestionList'>;
export type EstadoSugerencia = Sugerencia['status'];
export type MotivoSugerencia = Sugerencia['reason'];
export type DatosFusion = Esquema<'MergeCreate'>;
export type DatoQueSeQueda = NonNullable<DatosFusion['keep_from_absorbed']>[number];
export type Fusion = Esquema<'Merge'>;
export type FichaCliente = Esquema<'Customer'>;
export type ResumenDeCliente = Esquema<'CustomerSummary'>;
export type ListaDeClientes = Esquema<'CustomerSummaryList'>;
export type ListaDeNotas = Esquema<'NoteList'>;
export type ListaDeEvaluaciones = Esquema<'DoorAssessmentList'>;
export type ListaDeHistorial = Esquema<'TimelineEntryList'>;
export type LineaDeHistorial = Esquema<'TimelineEntry'>;
export type PermisoActual = Esquema<'ConsentCurrent'>;

const ruta = (businessId: IdNegocio, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = (valor: string) => encodeURIComponent(valor);

/**
 * Operaciones de «Duplicados» (2.5), con su `operationId` y los tipos generados de `clientes.yaml`. Las lecturas de la
 * ficha (getCustomer, listCustomers, notas, evaluaciones de puerta e historial) tienen la misma forma que las de 2.1–2.4
 * (`ops/clientes.ts`): las dos pantallas pueden usar cualquiera de las dos.
 */
export function operacionesClientesDuplicados(api: ClienteApi) {
    return {
        /** listDuplicateSuggestions (`clientes.duplicados.ver`): con los dos clientes (`customers`, G148); sin total (G140). */
        listDuplicateSuggestions: (businessId: IdNegocio, estado: EstadoSugerencia, pagina: Pagina = {}) =>
            api.pedir<ListaSugerencias>('GET', ruta(businessId, `/duplicate-suggestions${consulta({ status: estado, ...pagina })}`)),
        /** dismissDuplicateSuggestion (`clientes.duplicados.fusionar`): «Son personas distintas»; no se vuelve a sugerir. */
        dismissDuplicateSuggestion: (businessId: IdNegocio, suggestionId: string, clave = claveIdempotencia()) =>
            api.pedir<Sugerencia>('POST', ruta(businessId, `/duplicate-suggestions/${id(suggestionId)}/dismissal`), { idempotencia: clave }),
        /**
         * mergeCustomers (`clientes.duplicados.fusionar`): no se deshace desde el panel (G160). Sin el mismo teléfono ni email,
         * `409 confirmation_required` hasta repetirla con `confirm_different_person_risk: true` (G200).
         */
        mergeCustomers: (businessId: IdNegocio, datos: DatosFusion, clave = claveIdempotencia()) =>
            api.pedir<Fusion>('POST', ruta(businessId, '/customer-merges'), { cuerpo: datos, idempotencia: clave }),

        // ---- Lecturas de la ficha para comparar (misma forma que ops/clientes.ts) ----
    };
}
